import { apiClient } from './api/apiClient';
import { API_ENDPOINTS } from '../config/api';
import type { ServiceVersion } from '../models/ServiceVersion';

/**
 * Runtime info — manage-backend 호스트에서 현재 구동 중인 사이트 식별.
 * backend 가 docker socket 으로 smartparking-router.app 컨테이너를 inspect 해
 * NODE_ENV 값을 반환. 컨테이너 미기동 시 site=""/running=false.
 */
export interface CurrentSite {
  site: string;          // 'banpo' | 'central' | 'osong' | ''
  running: boolean;
  container_name: string;
  source: string;        // 예: 'env:NODE_ENV'
}

interface CurrentSiteResponse {
  success: boolean;
  message?: string;
  data?: CurrentSite;
}

/** 사내 배포 서버의 서비스별 실행 버전 — 한 번에 한 현장만 떠 있으므로 site 를 같이 준다. */
export interface CompanyServiceVersions {
  site: string;
  running: boolean;
  versions: ServiceVersion[];
}

interface ImageVersionsResponse {
  success: boolean;
  message?: string;
  data?: { appname: string; versions: string[] };
}

interface ServiceVersionsResponse {
  success: boolean;
  message?: string;
  data?: CompanyServiceVersions;
}

export class RuntimeService {
  static async getCurrentSite(): Promise<CurrentSite> {
    const response = await apiClient.get<CurrentSiteResponse>(
      API_ENDPOINTS.RUNTIME.CURRENT_SITE,
    );
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.message || 'current-site 조회 실패');
  }

  static async getServiceVersions(signal?: AbortSignal): Promise<CompanyServiceVersions> {
    const response = await apiClient.get<ServiceVersionsResponse>(
      API_ENDPOINTS.RUNTIME.SERVICE_VERSIONS,
      { signal },
    );
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.message || 'service-versions 조회 실패');
  }

  /** 사내 서버에 있는 <app>.app 의 vX.Y.Z 버전, 최신 먼저. */
  static async getImageVersions(appname: string, signal?: AbortSignal): Promise<string[]> {
    const response = await apiClient.get<ImageVersionsResponse>(
      API_ENDPOINTS.RUNTIME.IMAGE_VERSIONS(appname),
      { signal },
    );
    if (response.data.success && response.data.data) {
      return response.data.data.versions;
    }
    throw new Error(response.data.message || 'image-versions 조회 실패');
  }
}
